/**
 * Work tool/think steps in the transcript, in the order they happened.
 *
 * The segment still being written to (`isLive`) stays fully expanded; new
 * steps append in place with no mount animation, so the rows above do not
 * flash. Historical segments start compact; on mobile, a trail seen live
 * stays open until the reader folds it. Compact segments show a one-line
 * account ("Searched the web, read 6 pages · 12 steps") that
 * expands back to the full log on tap — the record is never gone, just
 * folded. Tiny segments are not worth a fold and stay expanded.
 */
import React, { useLayoutEffect, useRef, useState } from 'react';
import { View, Pressable, StyleSheet, Platform } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { filterWorkActivityLines, summarizeWorkSteps } from '@/constants/work';
import WorkSubagentCard from '@/components/Coworker/WorkSubagentCard';
import WorkCommandJobs from '@/components/Coworker/WorkCommandJobs';

function lineIcon(line) {
  if (line?.icon) return line.icon;
  if (line?.kind === 'turn') return 'lightbulb';
  if (line?.kind === 'info') return 'circle-info';
  if (line?.kind === 'divider') return 'circle-check';
  return 'arrow-right';
}

function sameActivityLine(prev, next) {
  return prev.isLive === next.isLive
    && prev.line?.key === next.line?.key
    && prev.line?.kind === next.line?.kind
    && prev.line?.text === next.line?.text
    && prev.line?.icon === next.line?.icon
    && prev.line?.status === next.line?.status
    && prev.line?.lastTool === next.line?.lastTool
    && prev.line?.model === next.line?.model
    && prev.line?.okCount === next.line?.okCount
    && prev.line?.partialCount === next.line?.partialCount
    && prev.line?.lastToolArgs?.path === next.line?.lastToolArgs?.path
    && prev.line?.lastToolArgs?.query === next.line?.lastToolArgs?.query
    && prev.line?.lastToolArgs?.operation === next.line?.lastToolArgs?.operation
    && JSON.stringify(prev.line?.lastToolArgs?.paths) === JSON.stringify(next.line?.lastToolArgs?.paths)
    && JSON.stringify(prev.line?.command) === JSON.stringify(next.line?.command)
    && prev.colors === next.colors;
}

const ActivityLine = React.memo(function ActivityLine({ line, colors, isLive = false }) {
  if (line.command && Platform.OS === 'macos') return <WorkCommandJobs command={line.command} isLive={isLive} />;
  if (line.kind === 'subagent') {
    return <WorkSubagentCard line={line} isLive={isLive} />;
  }

  if (line.kind === 'message' || line.kind === 'progress') {
    return (
      <View style={styles.row}>
        <Icon name="comment" size={14} color={colors.text_color_tertiary} style={styles.icon} />
        <Text size="n" color="secondary" numberOfLines={6} style={styles.text}>
          {line.text}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.row}>
      <Icon
        name={lineIcon(line)}
        size={14}
        color={colors.text_color_tertiary}
        style={styles.icon}
      />
      <Text size="n" color="tertiary" numberOfLines={3} style={styles.text}>
        {line.text}
      </Text>
    </View>
  );
}, sameActivityLine);

ActivityLine.propTypes = {
  line: PropTypes.object.isRequired,
  colors: PropTypes.object.isRequired,
  isLive: PropTypes.bool,
};

// A fold that hides fewer lines than this saves no space and costs a tap.
const COLLAPSE_FROM = 3;

function WorkActivityTrail({ lines = [], isLive = false, liveText = '' }) {
  const { colors } = useTheme();
  // Settling a live trail must not remove its rows beneath a mobile reader.
  // Historical trails still start compact, and the fold remains user-owned.
  const [expanded, setExpanded] = useState(null);
  const seenLive = useRef(isLive);
  useLayoutEffect(() => { if (isLive) seenLive.current = true; }, [isLive]);

  // The trail is an append-only log: every step stays exactly where it
  // happened. Folding is the user's to undo — nothing is dropped.
  const list = filterWorkActivityLines(lines, {
    hideCurrentText: isLive ? liveText : '',
  });

  // Keys: event seq when the line carries one, content otherwise. Index-based
  // keys remounted (and flickered) unchanged rows whenever the list shifted.
  const seen = new Map();
  const keyed = list.map((line) => {
    const base = line.key ?? `${line.kind}:${line.text}`;
    const count = seen.get(base) || 0;
    seen.set(base, count + 1);
    return { line, key: count ? `${base}~${count}` : String(base) };
  });

  if (!list.length) return null;

  const collapsible = !isLive && list.length >= COLLAPSE_FROM;
  const keepVisible = (Platform.OS === 'ios' || Platform.OS === 'android') && seenLive.current;
  const isOpen = !collapsible || (expanded ?? keepVisible);
  const summary = collapsible ? summarizeWorkSteps(list) : null;
  const summaryLabel = summary?.text || 'Worked through the steps';
  const countLabel = `${list.length} ${list.length === 1 ? 'step' : 'steps'}`;

  return (
    <View style={styles.trail}>
      {collapsible && (
        <Pressable
          onPress={() => setExpanded(!isOpen)}
          accessibilityRole="button"
          accessibilityLabel={`${summaryLabel} · ${countLabel}. ${isOpen ? 'Hide' : 'Show'} the steps`}
          style={({ hovered, pressed }) => [
            styles.foldRow,
            (hovered || pressed) && { backgroundColor: colors.solid_color_secondary },
          ]}
        >
          <Icon
            name={isOpen ? 'chevron-down' : 'chevron-right'}
            size={12}
            color={colors.text_color_tertiary}
            style={styles.foldIcon}
          />
          <Text size="n" color="secondary" numberOfLines={1} style={styles.text}>
            {summaryLabel}
            <Text size="n" color="tertiary">{` · ${countLabel}`}</Text>
          </Text>
        </Pressable>
      )}
      {isOpen && keyed.map(({ line, key }) => (
        <View key={key}>
          <ActivityLine line={line} colors={colors} isLive={isLive} />
        </View>
      ))}
    </View>
  );
}

WorkActivityTrail.propTypes = {
  lines: PropTypes.arrayOf(PropTypes.object),
  isLive: PropTypes.bool,
  liveText: PropTypes.string,
};

const styles = StyleSheet.create({
  trail: {
    gap: 6,
    marginTop: 2,
    marginBottom: 8,
    maxWidth: 560,
  },
  foldRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    paddingVertical: 3,
    paddingHorizontal: 6,
    marginLeft: -6,
    borderRadius: 6,
  },
  foldIcon: {
    marginTop: 4,
    width: 18,
    textAlign: 'center',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  icon: {
    marginTop: 3,
    width: 18,
  },
  text: {
    flex: 1,
  },
});

function trailSignature(lines) {
  if (!Array.isArray(lines) || !lines.length) return '';
  return lines.map((line) => (
    `${line?.key ?? ''}:${line?.kind ?? ''}:${line?.text ?? ''}:${line?.status ?? ''}:${line?.lastTool ?? ''}:${line?.okCount ?? ''}:${line?.partialCount ?? ''}:${line?.lastToolArgs?.path ?? ''}:${line?.lastToolArgs?.query ?? ''}:${line?.lastToolArgs?.operation ?? ''}:${JSON.stringify(line?.lastToolArgs?.paths)}:${JSON.stringify(line?.command)}`
  )).join('|');
}

export default React.memo(WorkActivityTrail, (prev, next) => (
  prev.isLive === next.isLive
  && prev.liveText === next.liveText
  && trailSignature(prev.lines) === trailSignature(next.lines)
));
